// Additional page-owned SuperPlumma adapters. Complex accounting decisions stay
// in the existing pages and backend mutations; this bridge exposes only bounded
// observations and exact, allowlisted operations.
const pageControlId = (value) => String(value || '');
const pageControlExactRows = (rows, ids) => {
  const wanted = Array.from(new Set((ids || []).map(pageControlId).filter(Boolean)));
  if (!wanted.length) throw new Error('pageControl.recordNotFound');
  const found = wanted.map((id) => rows.find((row) => pageControlId(row.id) === id)).filter(Boolean);
  if (found.length !== wanted.length) throw new Error('pageControl.recordNotFound');
  return found;
};

function useInboxPageControlAdapter(c) {
  const page = c.page;
  usePageOwnedControlAdapter(page, {
    read: () => ({
      view: c.view, query: c.query, selectedIds: c.selected,
      entries: c.entries().slice(0, 50).map((entry) => {
        const analysis = entry.receiptAnalysis || {};
        return {
          id: entry.id, name: entry.name || entry.attachmentName || entry.fileName || '',
          date: entry.date || analysis.receiptDate || entry.received || '', status: entry.status || analysis.status || '',
          supplierName: analysis.supplierName || '', gross: analysis.totalGross ?? analysis.totalAmount ?? null,
          readyToBook: page === 'inbox' && Boolean(c.canBook?.(entry)),
          duplicateWarning: Boolean(entry.possibleDuplicateExpenses?.length || entry.alreadyBookedMatch || entry.settlementBookedMatch),
          reviewReasons: [...(entry.review?.reasons || []), ...(entry.riskFlags || [])].slice(0, 10),
          attachmentCount: Array.isArray(entry.attachments) ? entry.attachments.length : 0,
        };
      }),
      paymentAccounts: page === 'inbox' ? pageControlPaymentAccounts() : [], busy: c.busy,
    }),
    perform: async (step) => {
      const f = step.fields || {}, rows = c.entries();
      if (step.action === 'set_fields') {
        if (f.query !== undefined) c.setQuery(f.query);
        if (f.view !== undefined) {
          const allowedViews = page === 'documentation' ? ['docs'] : ['inbox', 'trash'];
          if (!allowedViews.includes(f.view)) throw new Error('pageControl.unsupported');
          c.setView(f.view);
        }
        if (Array.isArray(f.recordIds)) { pageControlExactRows(rows, f.recordIds); c.setSelected(f.recordIds); }
        return { result: 'fields_applied' };
      }
      if (step.action === 'select_records') { pageControlExactRows(rows, f.recordIds); c.setSelected(f.recordIds); return { result: 'records_selected', count: f.recordIds.length }; }
      if (step.action === 'open_record' || step.action === 'open_review') {
        const entry = pageControlExactRows(rows, [f.entryId])[0]; c.setExpanded(entry.id); return { result: 'record_opened', recordId: entry.id };
      }
      if (step.action === 'open_attachment_picker') { c.openAttachmentPicker(); return { result: 'attachment_picker_opened' }; }
      if (step.action === 'analyze' && page === 'inbox') {
        const entry = pageControlExactRows(rows, [f.entryId])[0];
        if (!await c.analyze(entry)) throw new Error('pageControl.failed');
        return { result: 'analysis_completed', recordId: entry.id };
      }
      if (step.action === 'archive') {
        const ids = f.recordIds?.length ? f.recordIds : [f.entryId]; pageControlExactRows(rows, ids);
        if (!await c.archive(ids)) throw new Error('pageControl.failed');
        return { result: 'moved_to_trash', count: ids.length };
      }
      if (step.action === 'restore') {
        const ids = f.recordIds?.length ? f.recordIds : [f.entryId]; pageControlExactRows(rows, ids);
        if (!await c.restore(ids)) throw new Error('pageControl.failed');
        return { result: 'restored', count: ids.length };
      }
      if (step.action === 'open_dialog' && page === 'inbox') {
        const entry = pageControlExactRows(rows, [f.entryId])[0];
        const route = { open_purchase: 'nyttkjop', open_manual_posting: 'fripostering', open_other_sale: 'annetsalg' }[step.operation];
        if (!route) throw new Error('pageControl.unsupported'); c.routeEntry(entry, route);
        return { result: 'ordinary_form_opened', recordId: entry.id, route };
      }
      if (step.action === 'submit' && page === 'inbox' && step.operation === 'book') {
        const ids = f.recordIds?.length ? f.recordIds : [f.entryId];
        const selected = pageControlExactRows(rows, ids);
        if (selected.some((entry) => !c.canBook(entry) || entry.possibleDuplicateExpenses?.length || entry.alreadyBookedMatch || entry.settlementBookedMatch)) throw new Error('pageControl.reviewRequired');
        const booking = await c.book(ids, f.paymentAccountCode);
        if (!booking?.bookedIds?.length) throw new Error('pageControl.failed');
        return {
          result: booking.bookedIds.length === ids.length ? 'booked' : 'partially_booked',
          count: booking.bookedIds.length, skippedCount: Math.max(0, ids.length - booking.bookedIds.length),
          recordIds: booking.bookedIds,
        };
      }
      throw new Error('pageControl.unsupported');
    },
  });
}

function useProjectPageControlAdapter(c) {
  usePageOwnedControlAdapter('project', {
    read: () => ({
      tab: c.tab, draft: c.draft,
      projects: c.model.projects.slice(0, 50).map(({ id, name, projectNumber, customerId, contactPerson, startDate, endDate, description, color, status }) => ({ id, name, projectNumber, customerId, contactPerson, startDate, endDate, description, color, status })),
      activities: c.model.activities.slice(0, 100).map(({ id, projectId, name, productId, unitPrice, billing }) => ({ id, projectId, name, productId, unitPrice, billing })),
      timeEntries: c.model.entries.slice(0, 100).map(({ id, projectId, activityId, hours, date, note, invoiceStatus }) => ({ id, projectId, activityId, hours, date, note, invoiceStatus, locked: c.isLocked(date) })),
    }),
    perform: async (step) => {
      const f = step.fields || {};
      if (step.action === 'set_fields') {
        if (f.tab !== undefined) {
          if (!['time', 'oversikt', 'innst'].includes(f.tab)) throw new Error('pageControl.unsupported');
          c.setTab(f.tab);
        }
        if (['create_project', 'update_project'].includes(step.operation)) {
          const existing = f.projectId ? pageControlExactRows(c.model.projects, [f.projectId])[0] : null;
          c.setDraft({ kind: 'project', fields: { ...(existing || {}), ...f, id: existing?.id } });
        } else if (step.operation === 'save_time_entry') {
          const existing = f.timeEntryId ? pageControlExactRows(c.model.entries, [f.timeEntryId])[0] : null;
          c.setDraft({ kind: 'time_entry', fields: { ...(existing || {}), ...f, id: existing?.id } });
        }
        return { result: 'fields_applied' };
      }
      if (step.action === 'open_record') {
        if (f.timeEntryId) { const row = pageControlExactRows(c.model.entries, [f.timeEntryId])[0]; c.setDraft({ kind: 'time_entry', fields: row }); return { result: 'record_opened', recordId: row.id }; }
        const row = pageControlExactRows(c.model.projects, [f.projectId])[0]; c.setDraft({ kind: 'project', fields: row }); return { result: 'record_opened', recordId: row.id };
      }
      if (step.action === 'submit') {
        const draft = c.draft; if (!draft) throw new Error('pageControl.incomplete');
        let saved;
        if (draft.kind === 'project') { if (!String(draft.fields.name || '').trim()) throw new Error('pageControl.incomplete'); saved = await c.saveProject(draft.fields); }
        else { if (c.isLocked(draft.fields.date) || draft.fields.invoiceStatus === 'invoiced') throw new Error('pageControl.locked'); saved = await c.saveTimeEntry(draft.fields); }
        if (!saved) throw new Error('pageControl.failed'); c.setDraft(null);
        return { result: 'saved', recordId: saved.id || draft.fields.id || null };
      }
      if (step.action === 'delete' && step.operation === 'delete_time_entry') {
        const row = pageControlExactRows(c.model.entries, [f.timeEntryId])[0];
        if (c.isLocked(row.date) || row.invoiceStatus === 'invoiced') throw new Error('pageControl.locked');
        await c.deleteTimeEntry(row.id); return { result: 'deleted', recordId: row.id };
      }
      if (step.action === 'create_draft' && step.operation === 'create_invoice_draft') {
        const entries = pageControlExactRows(c.model.entries, f.recordIds);
        if (!entries.length || entries.some((row) => row.invoiceStatus === 'invoiced') || new Set(entries.map((row) => row.projectId)).size !== 1) throw new Error('pageControl.reviewRequired');
        const result = await c.createInvoiceDraft({ projectId: entries[0].projectId, entryIds: entries.map((row) => row.id), costKeys: [], includeTimesheet: f.includeTimesheet !== false });
        if (!result?.invoice) throw new Error('pageControl.failed'); c.openInvoice(result.invoice);
        return { result: 'invoice_draft_created', recordId: result.invoice.id || null };
      }
      throw new Error('pageControl.unsupported');
    },
  });
}

function useTransactionListPageControlAdapter(c) {
  usePageOwnedControlAdapter('transaction_list', {
    read: () => ({ year: c.year, view: c.view, query: c.search, onlyAnnulled: Boolean(c.filter?.checks.visannullerte), includeAnnulled: Boolean(c.filter?.checks.inkluderannullerte), totalMatches: c.rows().length, vouchers: c.rows().slice(0, 100).map(({ id, num, date, name, kind, status, gross, hasAttachment, sourceType, reversesEntryId, reversedByEntryId }) => ({ id, num, date, name, kind, status, gross, hasAttachment, sourceType, reversesEntryId, reversedByEntryId })) }),
    perform: async (step) => {
      const f = step.fields || {}, rows = c.rows();
      if (step.action === 'set_fields') {
        if (!Object.keys(f).length || Object.keys(f).some(key => !['query', 'view', 'onlyAnnulled', 'includeAnnulled'].includes(key))) throw new Error('pageControl.unsupported');
        if (['onlyAnnulled', 'includeAnnulled'].some(key => f[key] !== undefined && typeof f[key] !== 'boolean')) throw new Error('pageControl.unsupported');
        if (f.view !== undefined && !['bilag', 'detaljer', 'hovedbok'].includes(f.view)) throw new Error('pageControl.unsupported');
        if (f.onlyAnnulled !== undefined || f.includeAnnulled !== undefined) c.setFilter(previous => ({...previous, checks: {...previous.checks, ...(f.onlyAnnulled !== undefined ? {visannullerte:f.onlyAnnulled} : {}), ...(f.includeAnnulled !== undefined ? {inkluderannullerte:f.includeAnnulled} : {})}}));
        if (f.query !== undefined) c.setSearch(f.query);
        if (f.view !== undefined) {
          if (!['bilag', 'detaljer', 'hovedbok'].includes(f.view)) throw new Error('pageControl.unsupported');
          c.setView(f.view);
        }
        return { result: 'fields_applied' };
      }
      // The shared page-step contract also exposes entryId. Resolve either key
      // against the exact observed voucher ID, never against a source ID or name.
      if (f.voucherId && f.entryId && pageControlId(f.voucherId) !== pageControlId(f.entryId)) throw new Error('pageControl.recordNotFound');
      const voucher = pageControlExactRows(rows, [f.voucherId || f.entryId])[0];
      if (step.action === 'open_record') { c.openTransaction(voucher); return { result: 'transaction_opened', recordId: voucher.id }; }
      if (step.action === 'open_dialog' && step.operation === 'view_document') { if (!await c.openDocument(voucher)) throw new Error('pageControl.failed'); return { result: 'document_opened', recordId: voucher.id }; }
      if (step.action === 'open_attachment_picker') { c.openAttachmentPicker(voucher); return { result: 'attachment_picker_opened', recordId: voucher.id }; }
      throw new Error('pageControl.unsupported');
    },
  });
}

function useTransactionDetailPageControlAdapter(c) {
  usePageOwnedControlAdapter('transaction_detail', {
    read: () => ({ statusLabel: c.statusLabel, actions: { annul: { visible: c.canAnnul === true, enabled: c.canAnnul === true } }, voucher: c.voucher ? { id: c.voucher.id, num: c.voucher.num, date: c.voucher.date, name: c.voucher.name, kind: c.voucher.kind, status: c.voucher.status, gross: c.voucher.gross, sourceType: c.voucher.sourceType, hasAttachment: c.voucher.hasAttachment, reversesEntryId: c.voucher.reversesEntryId, reversedByEntryId: c.voucher.reversedByEntryId } : null }),
    perform: async (step) => {
      const f = step.fields || {}; if (!c.voucher || (f.voucherId && pageControlId(f.voucherId) !== pageControlId(c.voucher.id))) throw new Error('pageControl.recordNotFound');
      if (step.action === 'open_dialog' && step.operation === 'view_document') { if (!await c.openDocument()) throw new Error('pageControl.failed'); return { result: 'document_opened', recordId: c.voucher.id }; }
      if (step.action === 'open_dialog' && step.operation === 'open_annul') { if (c.canAnnul !== true || c.openAnnul() === false) throw new Error('pageControl.unsupported'); return { result: 'annul_review_opened', recordId: c.voucher.id }; }
      if (step.action === 'open_dialog' && step.operation === 'open_correction') { if (!c.openCorrection()) throw new Error('pageControl.unsupported'); return { result: 'correction_opened', recordId: c.voucher.id }; }
      throw new Error('pageControl.unsupported');
    },
  });
}

function useManualPostingPageControlAdapter(c) {
  usePageOwnedControlAdapter('manual_posting', {
    read: () => ({ postings: c.read(), saving: c.saving, error: c.error }),
    perform: async (step) => {
      if (step.action === 'set_fields' && step.operation === 'prepare_posting') { c.apply(step.fields || {}); return { result: 'fields_applied' }; }
      if (step.action === 'submit' && step.operation === 'post') { const saved = await c.save(); if (!saved?.length) throw new Error('pageControl.failed'); return { result: 'posted', recordIds: saved.map((row) => row.id).filter(Boolean) }; }
      throw new Error('pageControl.unsupported');
    },
  });
}

function useManualPostingPageControl(c) {
  useManualPostingPageControlAdapter({
    read: () => c.postings.map((posting) => ({
      id: posting.id, date: posting.date, description: posting.desc || '',
      lines: posting.lines.map((line) => ({
        fromAccountCode: window.PlummeManualPostingRules.accountCode(line.fraKonto),
        toAccountCode: window.PlummeManualPostingRules.accountCode(line.tilKonto),
        amount: Number(line.fraBelop) || Number(line.tilBelop) || 0,
        fromVatCode: line.fraMva || 'none', toVatCode: line.tilMva || 'none',
      })),
    })),
    apply: (fields) => {
      const postingLines = Array.isArray(fields.postingLines) ? fields.postingLines : [];
      if (!fields.date || !String(fields.description || '').trim() || !postingLines.length) throw new Error('pageControl.incomplete');
      const lines = postingLines.map((line, index) => ({
        ...fpNewLine({ fromAccountCode: line.fromAccountCode, toAccountCode: line.toAccountCode, amount: line.amount }),
        id: Date.now() + index, fraMva: line.fromVatCode || 'none', tilMva: line.toVatCode || 'none',
      }));
      const posting = { ...fpNewPosting({ date: fields.date, description: fields.description }), lines, expanded: lines[0].id };
      c.setEditPosting(null); c.setReceipt(null); c.setPostings([posting]);
    },
    save: c.save, saving: c.saving, error: c.error,
  });
}

function useAssetPageControlAdapter(c) {
  usePageOwnedControlAdapter('asset', {
    read: () => ({
      tab: c.tab, editor: c.editor,
      groups: c.groups.map(({ id, label, rate, assetAccountCode }) => ({ id, label, rate, assetAccountCode })),
      assets: c.assets.slice(0, 100).map(({ id, name, group, acct, cost, date, status, ut, dep, importedArchiveOnly }) => ({ id, name, group, accountCode: acct, cost, date, status, bookValue: ut, depreciation: dep, importedArchiveOnly })),
    }),
    perform: async (step) => {
      const f = step.fields || {};
      if (step.action === 'set_fields') {
        if (f.tab !== undefined) {
          if (!['aktive', 'avskrevne', 'solgte', 'alle'].includes(f.tab)) throw new Error('pageControl.unsupported');
          c.setTab(f.tab);
        }
        if (!['new_asset', 'edit_asset'].includes(step.operation)) return { result: 'fields_applied' };
        const asset = f.assetId ? pageControlExactRows(c.assets, [f.assetId])[0] : null;
        const fixedAsset = { ...(asset?.entry?.fixedAsset || {}) };
        if (f.readyForUseDate !== undefined) fixedAsset.readyForUseDate = f.readyForUseDate;
        if (f.usefulLifeMonths !== undefined) fixedAsset.usefulLifeMonths = f.usefulLifeMonths;
        if (f.residualValue !== undefined) fixedAsset.residualValue = f.residualValue;
        if (f.depreciationMethod !== undefined) fixedAsset.depreciationMethod = f.depreciationMethod;
        const entry = { ...(asset?.entry || {}), date: f.date ?? asset?.isoDate, fixedAsset };
        if (Array.isArray(f.lines)) entry.purchaseLines = f.lines.map((line) => ({
          description: line.description || '', amountNet: line.amountNet, vatRate: line.vatRate,
          costAccountCode: line.accountCode || '', reverseChargeCode: line.reverseChargeCode || '',
        }));
        if (f.supplierId !== undefined) entry.supplierId = f.supplierId;
        if (f.supplierName !== undefined) entry.supplierName = f.supplierName;
        if (f.invoiceNumber !== undefined) entry.supplierInvoiceNumber = f.invoiceNumber;
        if (f.paymentAccountCode !== undefined) entry.settlementAccountCode = f.paymentAccountCode;
        c.openEditor({
          ...(asset || {}), name: f.assetName ?? asset?.name ?? '', group: f.assetGroup ?? asset?.group,
          entry,
        });
        return { result: 'asset_form_prepared', recordId: asset?.id || null };
      }
      if (step.action === 'open_record') { const asset = pageControlExactRows(c.assets, [f.assetId])[0]; c.openRecord(asset); return { result: 'record_opened', recordId: asset.id }; }
      if (step.action === 'open_dialog' && step.operation === 'edit_asset') { const asset = pageControlExactRows(c.assets, [f.assetId])[0]; c.openEditor(asset); return { result: 'asset_form_opened', recordId: asset.id }; }
      throw new Error('pageControl.unsupported');
    },
  });
}

Object.assign(window, {
  useInboxPageControlAdapter, useProjectPageControlAdapter, useTransactionListPageControlAdapter,
  useTransactionDetailPageControlAdapter, useManualPostingPageControlAdapter, useManualPostingPageControl, useAssetPageControlAdapter,
});
